import {
  NotificationsPlugin,
  NotificationsPluginState,
} from "@pimo/notifications-plugin";
import {
  App,
  composeInitialState,
  LoginPlugin,
  LoginPluginPartialAppState,
  UniversalAppState,
} from "@pimo/pimo-app-builder";
import {
  type DefaultOverlayPartialAppState,
  initialDefaultOverlayState,
  type Log,
} from "@pimo/pimo-components";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import type {
  BffOe,
  BffOes,
  BffOverviewPage,
  Currency,
  DashboardFilterData,
  DashboardFilterOptions,
  Initiative,
  OperatingEntity,
  OverviewFilterData,
  OverviewFilterOptions,
  Profile,
} from "aint-types";

import { APP_KEY } from "../app-key";
import { APP_ROUTES } from "./constants";
import { STRAPI_URL } from "./env";
import { fetchOEs } from "./helpers/fetch-helpers.ts";
import { createOverlay } from "./overlay.ts";
import { DashboardPlugin } from "./pages/dashboard-plugin";
import { HomePlugin } from "./pages/home-plugin";
import {
  OeInitiativesPlugin,
  OEInitiativesPluginPartialState,
} from "./pages/oe-initiatives-plugin.ts";
import { OverviewPlugin } from "./pages/overview-plugin";
import { SettingsPlugin } from "./pages/settings-plugin.ts";
import { theme } from "./theme";

export type InAppState = DefaultOverlayPartialAppState &
  LoginPluginPartialAppState<Profile> &
  UniversalAppState &
  NotificationsPluginState &
  OEInitiativesPluginPartialState & {
    groupOverview?: BffOes;
    operatingEntities?: OperatingEntity[];
    oeOverview?: BffOverviewPage;
    dashboardFilterDialogData: DashboardFilterData;
    dashboardFilterDialogOptions: DashboardFilterOptions;
    overviewFilterData: OverviewFilterData;
    overviewFilterOptions: OverviewFilterOptions;
    logs?: Log[];
    currency?: Currency;
    currentInitiativePopup?: {
      showPopup?: boolean;
      content: Initiative | null;
    };
    editOeKpisPopup?: {
      showPopup?: boolean;
      content?: BffOe | null;
    };
    showUploadOeKpisPopup?: boolean;
    showUploadFXRatesPopup?: boolean;
  };

// Plugins
const loginPlugin = new LoginPlugin<Profile, InAppState>(
  APP_ROUTES.dashboard,
  STRAPI_URL,
  "Impact Navigator Tool",
  {
    automaticallyDetectEndedSessions: true,
  }
);

// App
const app = App.create<InAppState>(
  composeInitialState<InAppState>({
    ...initialDefaultOverlayState,
    ...loginPlugin.getInitialState(),
    isLoading: false,
    isSidebarOpen: true,
    currentEditInitiative: undefined,
    currentOE: undefined,
    supportingDocumentsDialogOpen: false,
    currentSupportingDocuments: [],
    currentUnsavedSupportingDocuments: [],
    initiativesLockDetails: {},
    showEditFormConfirmationDialog: false,
    editFormConfirmationDialogType: undefined,
    editFormNavigationPath: undefined,
    draftInitiativeToPublish: undefined,
    editFormLoading: true,
    editFormSaving: false,
    previousEditFormConfirmationDialogType: undefined,
    initiativeSortData: {
      key: "uid",
      direction: "ASC",
    },

    initiativeFilterData: {
      searchQueryFilter: "",
      yearFilter: "",
      phaseFilter: [],
      updateStatusFilter: [],
      businessUnitFilter: [],
      lineOfBusinessFilter: [],
      categoryFilter: [],
    },
    currentOEInitiatives: {
      initiatives: [],
      phasesOverview: {},
      updateStatusOverview: {
        completionStatusByEnabler: {},
        updateStatus: {},
        updateProgress: 0,
        numberOfInitiatives: 0,
      },
    },
    dashboardFilterDialogData: {
      fromDateFilter: [],
      untilDateFilter: [],
      oeProjectsFilter: [],
      functionsFilter: [],
      LoBsFilter: [],
      programsFilter: [],
      enablersFilter: [],
      baselinesFilter: [],
      regionsFilter: [],
    },
    dashboardFilterDialogOptions: {},
    overviewFilterData: {
      oeNameFilter: [],
      regionsFilter: [],
      searchQueryFilter: "",
    },
    overviewFilterOptions: {},
    groupOverview: {
      impactMetrics: {
        startDate: "",
        endDate: "",
        metrics: {
          totalImpact: { actual: 0, planned: 0 },
          tieServiceExpensesImpact: { actual: 0, planned: 0 },
          indemnityImpact: { actual: 0, planned: 0 },
          toplineImpact: { actual: 0, planned: 0 },
          externalFteImpact: { actual: 0, planned: 0 },
          internalFteImpact: { actual: 0, planned: 0 },
          commissionsImpact: { actual: 0, planned: 0 },
          investment: { actual: 0, planned: 0 },
        },
      },
      groupImpactValues: {
        totalImpact: [],
        tieServiceExpensesImpact: [],
        indemnityImpact: [],
        toplineImpact: [],
        commissionsImpact: [],
        investment: [],
        startYear: "",
        startMonth: 0,
      },
      groupEnablerOverview: {},
      groupPhasesOverview: {},
      groupUpdateStatus: { completed: 0, pending: 0, overdue: 0, total: 0 },
      numberOfInitiatives: 0,
      oeOverview: [],
      oes: [],
      initiatives: [],
    },
    operatingEntities: [],
  }),
  APP_KEY
);
app.setTheme(theme);

const overlay = createOverlay(app);

overlay.on("overlay:logout", () => {
  loginPlugin.logout();
});

loginPlugin.on("login-status-changed", async (evt) => {
  if (!evt.payload?.isLoggedIn) {
    return;
  }

  const groupOverview = await fetchOEs();

  app.patchAppState({
    operatingEntities: groupOverview.oes,
    isLoading: false,
  });
});

const homePlugin = new HomePlugin<InAppState>();
const dashboardPlugin = new DashboardPlugin();
const overviewPlugin = new OverviewPlugin();
const oeInitiativesPlugin = new OeInitiativesPlugin();
const resourceLockPlugin = new ResourceLockPlugin<InAppState>(STRAPI_URL);
const notificationsPlugin = new NotificationsPlugin<InAppState>(STRAPI_URL);
const settingsPlugin = new SettingsPlugin();

app.registerPlugin(loginPlugin);
app.registerPlugin(homePlugin);
app.registerPlugin(oeInitiativesPlugin);
app.registerPlugin(dashboardPlugin);
app.registerPlugin(overviewPlugin);
app.registerPlugin(resourceLockPlugin);
app.registerPlugin(notificationsPlugin);
app.registerPlugin(settingsPlugin);

const RenderedApp = app.render();

export default RenderedApp;
